import React, { useMemo } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildWebSummitDemoProofCase, isWebSummitDemoProofCaseId } from './demo/webSummitDemoProofCase';
import { humanizeAreaLabel } from '../components/proof/proofDisplayUtils';
import { SAFETY_REPORT_ADVANCED_DETAILS_SUBTITLE, SAFETY_REPORT_ADVANCED_DETAILS_TITLE } from './acceptanceProof/safetyReportUxCopy';

export type TechnicalDetailsFact = {
  label: string;
  value: string;
};

export type TechnicalDetailsSection = {
  title: string;
  facts: TechnicalDetailsFact[];
};

export type TechnicalDetailsModel = {
  title: string;
  subtitle: string;
  backToProofPath: string;
  bundleSettings: TechnicalDetailsSection;
  evidenceControls: TechnicalDetailsSection;
  advancedDiagnostics: TechnicalDetailsSection;
  rawDetails: TechnicalDetailsSection;
};

export type TechnicalDetailsRouteState = {
  technicalDetails?: TechnicalDetailsModel;
};

function customerFacingSafetyRuleLabel(raw?: string | null): string {
  const text = String(raw || '').trim();
  if (!text) return 'Safety event';
  if (/pedestrian in forklift area/i.test(text)) return 'Pedestrian Activity in Forklift Zone';
  if (/zone_violation|zone violation|danger-zone entry/i.test(text)) return 'Pedestrian Activity in Forklift Zone';
  if (/^incident$/i.test(text) || /incident activity/i.test(text)) return 'Safety event';
  return text;
}

function customerFacingAreaLabel(raw?: string | null): string {
  const text = String(raw || '').trim();
  if (!text) return '';
  if (/^unavailable$|^not captured yet$/i.test(text)) return '';
  if (/^demo5tes(\s+area)?$/i.test(text)) return 'Restricted Forklift Zone';
  if (/newfork.*pipel/i.test(text)) return 'Forklift operating area';
  return humanizeAreaLabel(text) || text;
}

function buildProofPackagePath(search: string, opts?: { refresh?: boolean }): string {
  const params = new URLSearchParams(search);
  params.set('surface', 'package');
  if (opts?.refresh) {
    params.set('refresh_evidence_pack', '1');
  } else {
    params.delete('refresh_evidence_pack');
  }
  const query = params.toString();
  return query ? `/acceptance/proof?${query}` : '/acceptance/proof?surface=package';
}

function fallbackTechnicalDetails(search: string): TechnicalDetailsModel {
  const params = new URLSearchParams(search);
  const caseId = String(params.get('caseId') || params.get('case_id') || '').trim();
  const site = String(params.get('site_id') || '').trim() || 'Not selected';
  const zone = customerFacingAreaLabel(String(params.get('zone_id') || '').trim()) || String(params.get('zone_id') || '').trim() || 'Not selected';
  const safetyRule = customerFacingSafetyRuleLabel(String(params.get('incident_type') || params.get('report_policy_type') || '').trim() || 'Not captured yet');
  const deviceId = String(params.get('device_id') || '').trim();
  const cameraId = String(params.get('camera_id') || '').trim();
  const reportFrom = String(params.get('report_from') || '').trim();
  const reportTo = String(params.get('report_to') || '').trim();
  const demoProofCase = isWebSummitDemoProofCaseId(caseId) ? buildWebSummitDemoProofCase(search, caseId) : null;
  const cameraSourceValue =
    demoProofCase?.cameraSourceLabel || [cameraId || null, deviceId || null].filter(Boolean).join(' / ') || 'Not captured yet';
  const selectedPeriod = reportFrom && reportTo ? `${reportFrom} to ${reportTo}` : 'Last 24h';

  return {
    title: SAFETY_REPORT_ADVANCED_DETAILS_TITLE,
    subtitle: SAFETY_REPORT_ADVANCED_DETAILS_SUBTITLE,
    backToProofPath: buildProofPackagePath(search),
    bundleSettings: {
      title: 'Scan setup',
      facts: [
        { label: 'Site', value: site },
        { label: 'Safety rule', value: safetyRule === 'Safety event' ? 'Not selected' : safetyRule },
        { label: 'Area', value: zone },
        { label: 'Selected period', value: selectedPeriod },
        { label: 'Scope source', value: caseId ? 'Current Review-linked scan' : 'Query fallback only' },
      ],
    },
    evidenceControls: {
      title: 'Evidence diagnostics',
      facts: [
        { label: 'Safety Scan status', value: demoProofCase?.evidencePackStatus || 'In progress' },
        { label: 'Last refreshed', value: demoProofCase?.timestamps.exportedAt || 'Not captured yet' },
        { label: 'Why this page exists', value: 'Use it only to troubleshoot scan setup, evidence diagnostics, or identifier-level details.' },
        { label: 'Source view', value: 'Return to the Safety Scan for the main customer-facing summary and download.' },
      ],
    },
    advancedDiagnostics: {
      title: 'System diagnostics',
      facts: [
        { label: 'Reliability metrics', value: demoProofCase?.reliabilityPackStatus || 'Waiting' },
        { label: 'Follow-up window', value: 'Open' },
        { label: 'Review posture', value: demoProofCase?.trustPosture || 'Needs review' },
        { label: 'Pipeline status', value: 'OK' },
      ],
    },
    rawDetails: {
      title: 'Raw identifiers',
      facts: [
        { label: 'Event IDs', value: 'Not captured yet' },
        { label: 'Camera/source IDs', value: cameraSourceValue },
        { label: 'Run/config version', value: 'Unavailable' },
        { label: 'Export timestamp', value: 'Not captured yet' },
      ],
    },
  };
}

const AcceptanceProofTechnicalDetails: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state || {}) as TechnicalDetailsRouteState;

  const details = useMemo(
    () => state.technicalDetails || fallbackTechnicalDetails(location.search),
    [location.search, state.technicalDetails],
  );

  const sections = [details.bundleSettings, details.evidenceControls, details.advancedDiagnostics, details.rawDetails];

  return (
    <Box sx={{ width: '100%', maxWidth: 'none', px: { xs: 2, sm: 3 }, pb: 3 }}>
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: -0.02 }}>
                {details.title}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5, maxWidth: 840 }}>
                {details.subtitle}
              </Typography>
            </Box>
            <Button variant="outlined" onClick={() => navigate(details.backToProofPath)}>
              Back to Draft Safety Scan
            </Button>
          </Stack>
        </Paper>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.5,
          }}
        >
          {sections.map((section) => (
            <Paper key={section.title} variant="outlined" sx={{ p: { xs: 1.25, sm: 1.5 }, borderRadius: 2 }}>
              <Stack spacing={1.25}>
                <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.02 }}>
                  {section.title}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gap: 1,
                  }}
                >
                  {section.facts.map((fact) => (
                    <Paper key={`${section.title}-${fact.label}`} variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {fact.label}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 700 }}>
                        {fact.value}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              </Stack>
            </Paper>
          ))}
        </Box>
      </Stack>
    </Box>
  );
};

export default AcceptanceProofTechnicalDetails;
